import { default as FetchService } from './FetchService';

export enum DexfAuthEndpoints {
	qa = 'https://api.dexf.qa.ikeadt.com/auth/v1',
	prod = 'https://api.dexf.ikea.com/auth/v1',
}
export interface IDexfAuthPing {
	data: string;
}
export interface IDexfAuth {
	access_token: string;
	id_token: string;
	expires_in: number;
	token_type: string;
}

/*
 * IKEA DEXF Auth service.
 *
 * DEXF Auth API:
 * https://app.swaggerhub.com/apis/DEXF/auth-dexf_api/3.2.1#/
 *
 * Example
 *	import {
 *		DexfAuthService,
 *		DexfAuthEndpoints,
 *	} from '../../services';
 *
 * 	const dexfAuthService = new DexfAuthService({
 *		dexfApiKey: '<dexf-api-key>',
 *		endpoint: DexfAuthEndpoints.qa,
 *	});
 *	console.log('DEXF application config', dexfAuthService.ping());
 */
export default class DexfAuthService {
	private dexfApiKey: string;
	private endpoint: DexfAuthEndpoints;
	private tokenData: { token: string; expires: number };

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.endpoint Endpoint.
	 * @param options.dexfApiKey DEXF API key.
	 */
	constructor(options: { dexfApiKey: string; endpoint: DexfAuthEndpoints }) {
		this.endpoint = options.endpoint;
		this.dexfApiKey = options.dexfApiKey;
	}

	/**
	 * This operation is used to ping the systemuser endpoint.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @returns True if it got pong.
	 */
	public async ping(): Promise<boolean> {
		const { data } = await this.pingData();
		return data === 'pong';
	}

	/**
	 * Gets token.
	 *
	 * @param username User name.
	 * @param password Password.
	 * @returns Id token.
	 */
	public async getToken(username: string, password: string): Promise<string> {
		if (this.tokenData && this.tokenData.expires > new Date().getTime()) {
			return this.tokenData.token;
		}
		const data = await this.getTokenData(username, password);
		if (data && data['id_token']) {
			this.tokenData = {
				token: data.id_token,
				expires: new Date().getTime() + data.expires_in * 1000,
			};
			return data['id_token'];
		}
		return '';
	}

	/**
	 * This operation is used to ping the systemuser endpoint.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @returns Pong, used for testing.
	 */
	private async pingData(): Promise<IDexfAuthPing> {
		const url = `${this.endpoint}/systemuser/ping`;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
			},
		})) as IDexfAuthPing;
	}

	/**
	 * This operation generates a access and id token to be able to access elevated services.
	 *
	 * @param username User name.
	 * @param password Password.
	 * @returns Token data.
	 */
	private async getTokenData(username: string, password: string): Promise<IDexfAuth> {
		const url = `${this.endpoint}/systemuser/token`;
		return (await FetchService.json(url, {
			headers: {
				accept: 'application/json',
				authorization: 'Basic ' + Buffer.from(username + ':' + password).toString('base64'),
				'dexf-api-key': this.dexfApiKey,
			},
		})) as IDexfAuth;
	}
}
